import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { getSavedMicrosoftProviderNames } from '../DataProvider/DataProvider.utils';
import {
  selectedSourceDetailsAtom,
  showInfoIconCommonIngestFromAtom
} from '../SourceCreate.store';
import { SourceDialogMode } from '../SourceCreate.types';

/**
 * Microsoft data provider paths that support ingest_from inheritance.
 * These providers can share a common ingest_from date from the root form.
 */
const MICROSOFT_DATA_PROVIDERS = [
  'data_provider.MicrosoftEntraID.GraphAPI',
  'data_provider.MicrosoftEntraID.O365API',
  'data_provider.O365',
  'data_provider.MicrosoftDefenderUnified'
] as const;

/**
 * Sentinel value indicating that providers have conflicting ingest_from dates.
 * When present, the UI shows an info icon to alert users about the discrepancy.
 */
const DIFFERENT_DATES_SENTINEL = '__DIFFERENT_DATES__';

/**
 * Extracts provider dates from selected Microsoft data providers.
 *
 * @param dataProviders - Array of provider paths
 * @param selectedProviders - Boolean array indicating which providers are selected
 * @param getValues - React Hook Form getter
 * @returns Array of ingest_from date strings from selected providers
 */
const getSelectedProviderDates = (
  dataProviders: readonly string[],
  selectedProviders: boolean[],
  getValues: (path: string) => unknown
): string[] => {
  const dates: string[] = [];

  dataProviders.forEach((providerPath, index) => {
    const isSelected = selectedProviders[index];
    if (!isSelected) return;

    const ingestFromPath = `${providerPath}.ingest_from`;
    const providerIngestFrom = getValues(ingestFromPath);

    if (providerIngestFrom && typeof providerIngestFrom === 'string') {
      dates.push(providerIngestFrom);
    }
  });

  return dates;
};

/**
 * Checks if all dates in the array represent the same calendar day.
 * Normalizes dates to UTC before comparison to handle timezone differences.
 *
 * @param dates - Array of date strings to compare
 * @returns true if all dates are on the same day (ignoring time), false otherwise
 */
const haveSameDate = (dates: string[]): boolean => {
  if (dates.length === 0) return false;

  const firstDate = dayjs.utc(dates[0]).local();
  return dates.every((date) =>
    dayjs.utc(date).local().isSame(firstDate, 'day')
  );
};

/**
 * Manages bidirectional ingest_from date inheritance for Microsoft data providers.
 *
 * **Business Rules:**
 *
 * 1. **Edit Mode - Root Population:**
 *    When editing an existing source with no common_ingest_from set, attempts to
 *    populate it from provider dates. If all selected providers share the same date,
 *    that date is promoted to common_ingest_from. If dates differ, shows an info icon.
 *
 * 2. **Date Inheritance to Providers:**
 *    When common_ingest_from is set, automatically propagates it to all selected
 *    providers UNLESS:
 *    - The provider field has been manually edited (dirty check)
 *    - The provider was already saved (exists in backend)
 *    - Root date is __DIFFERENT_DATES__ sentinel
 *
 * 3. **Conflict Detection:**
 *    If providers have different ingest_from dates, the UI shows an info icon
 *    to alert users. The sentinel value prevents overwriting user choices.
 *
 * @param mode - 'Edit' or 'New' - controls whether to populate root from providers
 */
const useIngestFromInheritance = (mode: string) => {
  const {
    setValue,
    getValues,
    control,
    formState: { dirtyFields }
  } = useFormContext();
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  const [, setShowInfoIconCommonIngestFrom] = useAtom(
    showInfoIconCommonIngestFromAtom
  );

  const rootIngestFrom = useWatch({ control, name: 'common_ingest_from' });

  const selectedProviders = useWatch({
    control,
    name: MICROSOFT_DATA_PROVIDERS.map((p) => `${p}.selected`)
  });

  /**
   * Checks if a form field has been manually edited by the user.
   * Uses React Hook Form's dirtyFields to track touched fields.
   */
  const isFieldDirty = useCallback(
    (path: string): boolean =>
      !!path.split('.').reduce((acc, key) => acc?.[key], dirtyFields),
    [dirtyFields]
  );

  useEffect(() => {
    // Guard: Ensure selectedProviders is properly initialized
    if (!selectedProviders || !Array.isArray(selectedProviders)) {
      return;
    }

    const hasSelectedProvider = selectedProviders.some(
      (isSelected) => isSelected
    );

    // Guard: No inheritance needed if no providers are selected
    if (!hasSelectedProvider) {
      return;
    }

    // === Edit Mode: Populate Root from Providers ===
    // When editing, if common_ingest_from is empty, try to derive it from provider dates
    if (mode === SourceDialogMode.Edit && !rootIngestFrom) {
      const providerDates = getSelectedProviderDates(
        MICROSOFT_DATA_PROVIDERS,
        selectedProviders,
        getValues
      );

      if (providerDates.length > 0) {
        if (haveSameDate(providerDates)) {
          // All providers share the same date - promote to root
          setValue('common_ingest_from', providerDates[0], {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false
          });
          setShowInfoIconCommonIngestFrom(false);
        } else {
          // Providers have conflicting dates - show info icon
          setShowInfoIconCommonIngestFrom(true);
        }
      }
      return;
    }

    // Guard: Don't inherit if root date is invalid or indicates conflicts
    if (!rootIngestFrom || rootIngestFrom === DIFFERENT_DATES_SENTINEL) {
      return;
    }

    // === Inherit Root Date to Providers ===
    // Propagate common_ingest_from to all selected, untouched, unsaved providers

    // Get list of providers that already exist in the backend (Edit mode only)
    const savedProviders =
      mode === SourceDialogMode.Edit && selectedSourceDetails
        ? getSavedMicrosoftProviderNames(selectedSourceDetails)
        : [];

    MICROSOFT_DATA_PROVIDERS.forEach((providerPath, index) => {
      const isSelected = selectedProviders[index];
      if (!isSelected) return;

      const ingestFromPath = `${providerPath}.ingest_from`;
      const providerKey = providerPath.split('.').pop() || '';

      // Skip if provider was manually edited by user
      if (isFieldDirty(ingestFromPath)) {
        return;
      }

      // Skip if provider already exists in backend (preserve existing data)
      if (savedProviders.includes(providerKey)) {
        return;
      }

      // Inherit root date to provider
      setValue(ingestFromPath, rootIngestFrom, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false
      });
    });
  }, [
    rootIngestFrom,
    selectedProviders,
    mode,
    getValues,
    isFieldDirty,
    setValue,
    selectedSourceDetails,
    setShowInfoIconCommonIngestFrom,
    dirtyFields
  ]);

  /**
   * Checks if providers have conflicting ingest_from dates.
   * Used by UI to conditionally show info icon.
   */
  const hasDifferentDates = useCallback(() => {
    return rootIngestFrom === DIFFERENT_DATES_SENTINEL;
  }, [rootIngestFrom]);

  return { hasDifferentDates };
};

export default useIngestFromInheritance;
